import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { Controller, useFormContext } from 'react-hook-form';
import { OptionsGroup } from '@/vacancy_request/components/template-request-form/options-group';
import { optionsList, optionsValues } from '@/vacancy_request/components/template-request-form/options-group/constants';
import { Input } from '@components/form/input';
import { SelectForm } from '@components/form/select';
import { Checkbox } from '@components/form/checkbox';
import { HrDarkGrey, HrFlexContainer, HrPrimary, HrRed, HrTitleH6, Separate } from '@components/styles/hr-styles';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import { getDataForRequests, createArea, createClient } from '@/vacancy_request/services/api';
import { useToast } from '@/hook/useToast';
import { CustomFieldsSection } from '@/vacancy_request/components/template-request-form/custom-fields/custom-fields-section';
import { CUSTOM_FIELD_TABS } from '@/vacancy_request/components/template-request-form/custom-fields/constants';
import { CreateAccountEntityModal } from './create-account-entity-modal';

const ApprovalCheckboxWrapper = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const ApprovalCheckboxWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HrInfoTooltip = styled(ReactTooltip)`
  background-color: #ffffff !important;
  color: #3b3f51 !important;
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: left;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  border: 1px solid #e6e6e6;

  &.place-top:after {
    border-top-color: #ffffff !important;
  }
  &.place-bottom:after {
    border-bottom-color: #ffffff !important;
  }
  &.place-left:after {
    border-left-color: #ffffff !important;
  }
  &.place-right:after {
    border-right-color: #ffffff !important;
  }
`;

const normalizeResponseCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') return [payload];
  return [];
};

const GeneralInformationTab = (props) => {
  const { disabled } = props;
  const { register, formState, watch, setValue, control } = useFormContext();
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.stepOne');
  const { showToast } = useToast();
  const areaValue = watch('area');
  const subareaValue = watch('subarea');
  const countryValue = watch('country');
  const stateValue = watch('zona');
  const localityValue = watch('locality');
  const templateAreaId = watch('templateAreaId');
  const templateClientId = watch('templateClientId');
  const templateSubareaId = watch('templateSubareaId');
  const templatePortalData = watch('templatePortalData');
  const areaApprovalRequired = watch('areaApprovalRequired');
  const subareaApprovalRequired = watch('subareaApprovalRequired');
  const [accountType, setAccountType] = useState('company');
  const [portalDataOptions, setPortalDataOptions] = useState([]);
  const [accountEntityOptions, setAccountEntityOptions] = useState([]);
  const [allSubentityOptions, setAllSubentityOptions] = useState([]);
  const [showCreateEntityModal, setShowCreateEntityModal] = useState(false);
  const [isSubmittingEntity, setIsSubmittingEntity] = useState(false);
  const [hasLoadedAccountEntities, setHasLoadedAccountEntities] = useState(false);

  const aliasAccount = window.location.host.split('.')[0];
  const isCompanyAccount = accountType === 'company';
  const accountEntityFieldName = isCompanyAccount ? 'templateAreaId' : 'templateClientId';
  const selectedAccountEntityValue = isCompanyAccount ? templateAreaId : templateClientId;
  const accountEntityLabel = isCompanyAccount ? 'Área' : 'Cliente';
  const subentityLabel = isCompanyAccount ? 'Subárea' : 'Subcliente';
  const addAccountEntityLabel = isCompanyAccount ? 'Crear área' : 'Crear cliente';

  const sortedAccountEntityOptions = useMemo(
    () =>
      [...accountEntityOptions].sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label)
      ),
    [accountEntityOptions]
  );
  const hasAvailableAccountEntities = sortedAccountEntityOptions.length > 0;

  const sortedSubentityOptions = useMemo(() => {
    if (!selectedAccountEntityValue) return [];

    const filteredOptions = allSubentityOptions
      .filter((subentity) => {
        const parentId = isCompanyAccount
          ? subentity.area ?? subentity.areaId
          : subentity.clienteId ?? subentity.client ?? subentity.clientId;

        return `${parentId}` === `${selectedAccountEntityValue}`;
      })
      .map((subentity) => ({
        value: subentity.id,
        label: subentity.nombre
      }));

    return filteredOptions.sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label));
  }, [allSubentityOptions, isCompanyAccount, selectedAccountEntityValue]);

  const hasAvailableSubentities = sortedSubentityOptions.length > 0;
  const disabledValue = optionsValues.DISABLED.toString();
  const shouldShowAreaComponent = areaValue !== disabledValue;

  useEffect(() => {
    const requiredValue = optionsValues.REQUIRED.toString();

    if (areaValue === undefined || areaValue === null || areaValue === '') {
      setValue('area', requiredValue);
    }
  }, [areaValue, setValue]);

  useEffect(() => {
    if (!hasLoadedAccountEntities) return;

    const optionalValue = optionsValues.OPTIONAL.toString();

    if (subareaValue === disabledValue && !hasAvailableSubentities) {
      if (templateSubareaId) {
        setValue('templateSubareaId', '');
      }
      if (subareaApprovalRequired) {
        setValue('subareaApprovalRequired', false);
      }
      return;
    }

    if (hasAvailableSubentities) {
      if (subareaValue !== optionalValue) {
        setValue('subarea', optionalValue);
      }
      return;
    }

    if (subareaValue !== disabledValue) {
      setValue('subarea', disabledValue);
    }
    if (templateSubareaId) {
      setValue('templateSubareaId', '');
    }
    if (subareaApprovalRequired) {
      setValue('subareaApprovalRequired', false);
    }
  }, [hasAvailableSubentities, hasLoadedAccountEntities, setValue, subareaApprovalRequired, subareaValue, templateSubareaId]);

  useEffect(() => {
    const disabledValue = optionsValues.DISABLED.toString();
    const optionalValue = optionsValues.OPTIONAL.toString();
    const requiredValue = optionsValues.REQUIRED.toString();

    if (countryValue === disabledValue) {
      if (stateValue !== disabledValue) {
        setValue('zona', disabledValue);
      }
      if (localityValue !== disabledValue) {
        setValue('locality', disabledValue);
      }
      return;
    }

    if (countryValue === optionalValue && stateValue === requiredValue) {
      setValue('zona', optionalValue);
    }

    const nextStateValue = countryValue === optionalValue && stateValue === requiredValue ? optionalValue : stateValue;

    if (nextStateValue === disabledValue) {
      if (localityValue !== disabledValue) {
        setValue('locality', disabledValue);
      }
      return;
    }

    if (nextStateValue === optionalValue && localityValue === requiredValue) {
      setValue('locality', optionalValue);
    }
  }, [countryValue, localityValue, setValue, stateValue]);

  useEffect(() => {
    let isMounted = true;

    const loadAccountEntities = async () => {
      try {
        const response = await getDataForRequests(aliasAccount);
        if (!isMounted || !response) return;

        const nextAccountType = response.accountType || 'company';
        const portals = Array.isArray(response.portalData) ? response.portalData : [];
        const areas = normalizeResponseCollection(response.areas?.response?.areas);
        const clients = normalizeResponseCollection(response.clients?.response?.clientes);
        const subareas = normalizeResponseCollection(response.subareas?.response?.subareas);
        const subclients = normalizeResponseCollection(response.subclients?.response?.subclientes);
        const nextOptions =
          nextAccountType === 'consultant'
            ? clients.map((client) => ({
              value: client.id,
              label: client.compañia
            }))
            : areas.map((area) => ({
              value: area.id,
              label: area.nombre
            }));
        const nextSubentityOptions =
          nextAccountType === 'consultant' ? subclients : subareas;

        setAccountType(nextAccountType);
        setValue('accountType', nextAccountType);
        setPortalDataOptions(portals);
        setAccountEntityOptions(nextOptions);
        setAllSubentityOptions(nextSubentityOptions);
        if (nextAccountType === 'consultant') {
          setValue('templateAreaId', '');
        } else {
          setValue('templateClientId', '');
        }
        setHasLoadedAccountEntities(true);
      } catch (error) {
        console.error('Error loading template account entities', error);
        setHasLoadedAccountEntities(true);
      }
    };

    loadAccountEntities();

    return () => {
      isMounted = false;
    };
  }, [aliasAccount, setValue]);

  useEffect(() => {
    if (!hasLoadedAccountEntities || !templatePortalData || !portalDataOptions.length) return;
    const matched = portalDataOptions.find((o) => `${o.value}` === `${templatePortalData}`);
    if (matched?.label) {
      setValue('templatePortalDataName', matched.label);
    }
  }, [hasLoadedAccountEntities, portalDataOptions, templatePortalData, setValue]);

  useEffect(() => {
    if (!hasLoadedAccountEntities) return;

    if (!selectedAccountEntityValue) {
      if (areaApprovalRequired) {
        setValue('areaApprovalRequired', false);
      }
      if (templateSubareaId) {
        setValue('templateSubareaId', '');
      }
      if (subareaApprovalRequired) {
        setValue('subareaApprovalRequired', false);
      }
      return;
    }

    const currentSubentityIsValid = sortedSubentityOptions.some((option) => `${option.value}` === `${templateSubareaId}`);
    if (!currentSubentityIsValid && templateSubareaId) {
      setValue('templateSubareaId', '');
    }

    if (sortedSubentityOptions.length === 0 && subareaApprovalRequired) {
      setValue('subareaApprovalRequired', false);
    }
  }, [
    areaApprovalRequired,
    hasLoadedAccountEntities,
    selectedAccountEntityValue,
    setValue,
    subareaApprovalRequired,
    sortedSubentityOptions,
    templateSubareaId,
  ]);

  const buildDependentOptionDisabler = (parentValue) => (optionValue) => {
    const disabledValue = optionsValues.DISABLED.toString();
    const optionalValue = optionsValues.OPTIONAL.toString();
    const requiredValue = optionsValues.REQUIRED.toString();

    if (parentValue === disabledValue) {
      return optionValue !== disabledValue;
    }

    if (parentValue === optionalValue) {
      return optionValue === requiredValue;
    }

    return false;
  };

  const isStateOptionDisabled = buildDependentOptionDisabler(countryValue);
  const isLocalityOptionDisabled = buildDependentOptionDisabler(stateValue);

  const toggleCreateEntityModal = () => setShowCreateEntityModal((previousValue) => !previousValue);

  const handleCreateEntity = async (data) => {
    try {
      setIsSubmittingEntity(true);

      const payload = {
        ...data,
        userId: window?.s_user?._id
      };
      const response = isCompanyAccount ? await createArea(payload) : await createClient(payload);
      const { result, response: entityResponse, message } = response.data || {};
      const createdEntityId = entityResponse?.result?.id;

      if (result !== 'success' || !createdEntityId) {
        const errors = Array.isArray(message) ? message : message?.errors;
        const duplicateField = isCompanyAccount ? 'area' : 'empresa';
        const isDuplicate = errors?.some((error) => error.field === duplicateField);

        showToast({
          color: HrRed,
          iconClassName: 'hr-Alert',
          message: isDuplicate
            ? `Ya existe un ${isCompanyAccount ? 'área' : 'cliente'} con este nombre en tu cuenta.`
            : `No se pudo crear el ${isCompanyAccount ? 'área' : 'cliente'}`
        });
        return false;
      }

      const createdEntityOption = {
        value: createdEntityId,
        label: isCompanyAccount ? data.area : data.empresa
      };

      setAccountEntityOptions((previousOptions) => [...previousOptions, createdEntityOption]);
      setValue(accountEntityFieldName, createdEntityId);
      setShowCreateEntityModal(false);

      showToast({
        color: HrPrimary,
        iconClassName: 'hr-Checkmark',
        message: isCompanyAccount ? 'Área creada con éxito' : 'Cliente creado con éxito'
      });

      return true;
    } catch (error) {
      console.error('Error creating account entity for vacancy request template', error);
      showToast({
        color: HrRed,
        iconClassName: 'hr-Alert',
        message: `No se pudo crear el ${isCompanyAccount ? 'área' : 'cliente'}`
      });

      return false;
    } finally {
      setIsSubmittingEntity(false);
    }
  };

  return (
    <>
      {/* Nombre del template */}
      <Input
        type='text'
        name='template_name'
        register={register}
        placeholder={t('fields.templateName.placeholder')}
        title={t('fields.templateName.label')}
        formState={formState}
        disabled={disabled}
        validate={{
          required: true,
          minLength: 5,
          maxLength: 100,
          whiteSpace: true,
          caracterSpecial: true
        }}
      />
      <Separate margin='0' />

      {/* Unidad de negocio / Micrositio */}
      <Controller control={control} name='templatePortalDataName' defaultValue='' render={() => null} />
      <HrFlexContainer alignSelf='flex-start'>
        <SelectForm
          title={t('fields.businessGroup.label')}
          id='templatePortalData'
          data={portalDataOptions}
          control={control}
          formState={formState}
          isSearchable
          validate={{ required: false }}
          defaultValue={templatePortalData || null}
          disabled={disabled || portalDataOptions.length === 0}
          width='300px'
          onChangee={({ value, label }) => {
            setValue('templatePortalData', value, { shouldDirty: true, shouldValidate: true });
            setValue('templatePortalDataName', label ?? '');
          }}
          errorPosition='bottom'
        />
      </HrFlexContainer>
      <Separate margin='0' />

      {/* Nombre del solicitante */}
      <OptionsGroup
        name='applicantName'
        label={t('fields.applicantName.label')}
        options={optionsList}
        disabled={disabled}
      />
      <Separate margin='0' />

      {/* Email */}
      <OptionsGroup
        name='applicantEmail'
        label={t('fields.applicantEmail.label')}
        options={optionsList}
        disabled={disabled}
      />
      <Separate margin='0' />

      {shouldShowAreaComponent && (
        <>
          {/* Área o cliente */}
          <HrFlexContainer flexWrap='nowrap' alignItems='center' justifyContent='space-between'>
            <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='8px'>
              <SelectForm
                key={accountEntityFieldName}
                title={accountEntityLabel}
                id={accountEntityFieldName}
                data={sortedAccountEntityOptions}
                control={control}
                formState={formState}
                isSearchable
                validate={{ required: true }}
                defaultValue={selectedAccountEntityValue}
                disabled={disabled || !hasAvailableAccountEntities}
                width='300px'
                onChangee={({ value }) => {
                  setValue(accountEntityFieldName, value, { shouldDirty: true, shouldValidate: true });
                  setValue('templateSubareaId', '');
                }}
                errorPosition='bottom'
              />
              <HrFlexContainer justifyContent='space-between'>
                {!disabled && (
                  <HrTitleH6 color={HrPrimary} cursor='pointer' onClick={toggleCreateEntityModal} fontWeight='600'>
                    + {addAccountEntityLabel}
                  </HrTitleH6>
                )}
              </HrFlexContainer>
            </HrFlexContainer>
            {isCompanyAccount && (
              <ApprovalCheckboxWrapper>
                <ApprovalCheckboxWithInfo>
                  <Checkbox
                    label='Necesita aprobación del área'
                    name='areaApprovalRequired'
                    control={control}
                    defaultChecked={false}
                    disabled={disabled || !selectedAccountEntityValue}
                    formState={formState}
                    width='auto'
                  />
                  <span
                    className='hr-Info'
                    style={{ cursor: 'help', color: HrDarkGrey }}
                    data-tip='Se enviará automáticamente un mail al líder del área solicitando su aprobación.'
                    data-for='tooltip-template-area-approval'
                    aria-label='Información: aprobación del área'
                  />
                  <HrInfoTooltip id='tooltip-template-area-approval' place='top' effect='solid' delayShow={200} />
                </ApprovalCheckboxWithInfo>
              </ApprovalCheckboxWrapper>
            )}
          </HrFlexContainer>
          <Separate margin='0' />
        </>
      )}

      {shouldShowAreaComponent && (
        <>
          {/* Subárea */}
          <HrFlexContainer flexWrap='nowrap' alignItems='center' justifyContent='space-between'>
            <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='8px'>
              <SelectForm
                key={`${accountEntityFieldName}-subentity`}
                title={subentityLabel}
                id='templateSubareaId'
                data={sortedSubentityOptions}
                control={control}
                formState={formState}
                isSearchable
                validate={{ required: false }}
                defaultValue={templateSubareaId}
                disabled={disabled || !hasAvailableSubentities}
                width='300px'
                onChangee={({ value }) => {
                  setValue('templateSubareaId', value, { shouldDirty: true, shouldValidate: true });
                }}
                errorPosition='bottom'
              />
              <HrFlexContainer justifyContent='space-between' />
            </HrFlexContainer>
            {isCompanyAccount && (
              <ApprovalCheckboxWrapper>
                <ApprovalCheckboxWithInfo>
                  <Checkbox
                    label='Necesita aprobación de la subárea'
                    name='subareaApprovalRequired'
                    control={control}
                    defaultChecked={false}
                    disabled={disabled || !hasAvailableSubentities || !templateSubareaId}
                    formState={formState}
                    width='auto'
                  />
                  <span
                    className='hr-Info'
                    style={{ cursor: 'help', color: HrDarkGrey }}
                    data-tip='Se enviará automáticamente un mail al líder de la subárea solicitando su aprobación.'
                    data-for='tooltip-template-subarea-approval'
                    aria-label='Información: aprobación de la subárea'
                  />
                  <HrInfoTooltip id='tooltip-template-subarea-approval' place='top' effect='solid' delayShow={200} />
                </ApprovalCheckboxWithInfo>
              </ApprovalCheckboxWrapper>
            )}
          </HrFlexContainer>
          <Separate margin='0' />
        </>
      )}

      {/* País */}
      <OptionsGroup name='country' label={t('fields.country.label')} options={optionsList} disabled={disabled} />
      <Separate margin='0' />

      {/* Provincia / estado */}
      <OptionsGroup
        name='zona'
        label={t('fields.state.label')}
        options={optionsList}
        disabled={disabled}
        isOptionDisabled={isStateOptionDisabled}
      />
      <Separate margin='0' />

      {/* Ciudad */}
      <OptionsGroup
        name='locality'
        label={t('fields.city.label')}
        options={optionsList}
        disabled={disabled}
        isOptionDisabled={isLocalityOptionDisabled}
      />
      <Separate margin='0' />

      {/* Campos personalizados */}
      <CustomFieldsSection tab={CUSTOM_FIELD_TABS.GENERAL} disabled={disabled} />

      <CreateAccountEntityModal
        entityType={isCompanyAccount ? 'area' : 'client'}
        isOpen={showCreateEntityModal}
        onClose={toggleCreateEntityModal}
        onSubmit={handleCreateEntity}
        isSubmitting={isSubmittingEntity}
      />
    </>
  );
};

GeneralInformationTab.propTypes = {
  disabled: PropTypes.bool
};

GeneralInformationTab.defaultProps = {
  disabled: false
};

export default GeneralInformationTab;
